import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '@/features/auth/session';
import { Skeleton } from '@/components/ui/skeleton';
import type { PlatformCapability } from '@/lib/platform-capabilities';
import { useHasPlatformCapability } from '@/lib/platform-capabilities';

/**
 * Gates a PLATFORM-scoped route branch (G-9, Job 6) - deliberately a
 * sibling of `RequireActiveOrganization` in the route tree, never nested
 * inside it, so a Platform Super Admin with zero organization memberships
 * is never forced through Active Organization resolution to reach it.
 *
 * Sits inside `ProtectedRoute` (authentication already established), but
 * still checks `useAuth().status` itself, exactly like `ProtectedRoute`
 * does: while the session is still being restored, `user` is briefly
 * `null` and `useHasPlatformCapability` would evaluate to `false` -
 * redirecting on that transient state, rather than waiting for it to
 * resolve, would bounce an already-authorized Platform Super Admin to
 * `/404` on every page reload (the exact bug `ProtectedRoute`'s own doc
 * comment warns about, reproduced here once and fixed the same way).
 *
 * Redirects a non-platform-capable authenticated caller to `/404` rather
 * than a distinct "forbidden" page - the same not-found-not-forbidden
 * masking the backend already applies to cross-tenant access, so this
 * route's existence is not itself disclosed to a caller who cannot use it.
 * The backend remains the actual authority: every mutation this page
 * triggers is independently re-checked server-side regardless of what
 * renders here.
 */
export function RequirePlatformCapability({
  capability,
}: {
  readonly capability: PlatformCapability;
}): ReactNode {
  const { status } = useAuth();
  const hasCapability = useHasPlatformCapability(capability);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status">
        <Skeleton className="h-8 w-32" />
      </div>
    );
  }

  if (!hasCapability) {
    return <Navigate to="/404" replace />;
  }

  return <Outlet />;
}
